import LocationGetSettingsResponse from "./LocationResponses/LocationGetSettingsResponse";
import LocationGetUsernamesResponse from "./LocationResponses/LocationGetUsernamesResponse";
import RepositoryBase from "./RepositoryBase";
import { ResponseEnvelope } from "./RepositoryBase";
import { DEFAULT_EXACT_CALCULATION, sanitizeCurrencies } from "../Types/CurrencyDefaults";

export default class LocationRepository extends RepositoryBase {
    public static async getLocationName(locationId: string): Promise<ResponseEnvelope<string>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<string>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/location/${locationId}/name`, {
                method: 'GET',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
            })

            if (!response.ok) return this.invalidResponse<string>();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<string>(
                true,
                data.name
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getUsernames(locationId: string): Promise<ResponseEnvelope<LocationGetUsernamesResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<LocationGetUsernamesResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/location/${locationId}/users`, {
                method: 'GET',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
            })

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope(
                true,
                {
                    users: data.users
                }
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getLocationSettings(locationId: string): Promise<ResponseEnvelope<LocationGetSettingsResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<LocationGetSettingsResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/location/${locationId}/settings`, {
                method: 'GET',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
            })

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope(
                true,
                {
                    cashPeriod: data.cashPeriod,
                    pinPeriod: data.pinPeriod,
                    currencies: sanitizeCurrencies(data.currencies),
                    exactCalculation: typeof data.exactCalculation === "boolean" ? data.exactCalculation : DEFAULT_EXACT_CALCULATION
                }
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async updateLocationSettings(locationId: string, settings: { cashPeriod?: number; pinPeriod?: number; countPeriod?: number; currencies?: number[]; exactCalculation?: boolean }): Promise<ResponseEnvelope<void>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<void>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            // Only send the fields that are actually being changed. Sending the
            // untouched settings as null made a currency-only update carry three
            // null period fields, which the API rejects.
            const body: Record<string, unknown> = {};
            if (settings.cashPeriod !== undefined) body.cashPeriod = settings.cashPeriod;
            if (settings.pinPeriod !== undefined) body.pinPeriod = settings.pinPeriod;
            if (settings.countPeriod !== undefined) body.countPeriod = settings.countPeriod;
            if (settings.currencies !== undefined) body.currencies = settings.currencies;
            if (settings.exactCalculation !== undefined) body.exactCalculation = settings.exactCalculation;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/location/${locationId}/settings`, {
                method: 'PUT',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify(body)
            })

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<void>(true);
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }
}
